'use client';

import { Check, Minus, Plus, X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';

import {
  READER_FONT_SIZE_MAX,
  READER_FONT_SIZE_MIN,
  READER_FONT_SIZE_STEP,
  READER_LINE_HEIGHT_MAX,
  READER_LINE_HEIGHT_MIN,
  READER_LINE_HEIGHT_STEP,
  READER_THEMES,
  READER_THEME_IDS,
  formatReaderLineHeight,
  snapReaderFontSize,
  snapReaderLineHeight,
  type ReaderDisplaySettings,
  type ReaderThemeId,
} from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';

const settingsIconButtonClassName =
  'inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-transparent text-white hover:bg-[rgba(17,24,39,0.06)]';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface ReaderSettingsPanelProps {
  settings: ReaderDisplaySettings;
  onThemeChange: (theme: ReaderThemeId) => void;
  onFontSizeChange: (fontSize: number) => void;
  onLineHeightChange: (lineHeight: number) => void;
  onReset: () => void;
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLElement | null>;
}

export const ReaderSettingsPanel = ({
  settings,
  onThemeChange,
  onFontSizeChange,
  onLineHeightChange,
  onReset,
  onClose,
  returnFocusRef,
}: ReaderSettingsPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const themeGroupId = useId();
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const settingsButton = returnFocusRef.current;

    const focusables = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.tabIndex !== -1 && !element.hasAttribute('disabled'),
      );

    focusables()[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      settingsButton?.focus();
    };
  }, [returnFocusRef]);

  const changeFontSize = (next: number) => {
    const snapped = snapReaderFontSize(next);
    if (snapped === null) return;
    onFontSizeChange(snapped);
  };

  return (
    <div className="absolute inset-0 z-[60]">
      <div className="absolute inset-0 bg-transparent max-sm:bg-black/40" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute bottom-2.5 right-2.5 top-2.5 flex w-[460px] max-w-[calc(100%-20px)] flex-col overflow-hidden rounded-3xl border border-white/[0.08] bg-[#171a22] text-white shadow-[0_24px_60px_rgba(0,0,0,0.35)] contrast-more:border-2 contrast-more:border-current max-sm:bottom-[max(8px,env(safe-area-inset-bottom))] max-sm:left-[max(8px,env(safe-area-inset-left))] max-sm:right-[max(8px,env(safe-area-inset-right))] max-sm:top-[max(8px,env(safe-area-inset-top))] max-sm:w-auto max-sm:max-w-none"
      >
        <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#FF3DAE]">
              Читалка Molwe
            </p>
            <h2 id={titleId} className="mt-1 text-[28px] font-semibold leading-tight text-white">
              Настройки чтения
            </h2>
          </div>
          <button
            type="button"
            className={settingsIconButtonClassName}
            onClick={onClose}
            aria-label="Закрыть настройки"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 pb-3 pt-2">
          <div className="mt-2">
            <h3 className="text-base font-semibold text-white">Тема</h3>
            <p className="mt-1 text-sm leading-5 text-[#9CA3AF]">
              Фон и цвет текста меняются вместе — сочетания уже настроены для чтения.
            </p>
            <div
              id={themeGroupId}
              role="radiogroup"
              aria-label="Тема чтения"
              className="mt-4 grid grid-cols-2 gap-3"
            >
              {READER_THEME_IDS.map((themeId) => {
                const theme = READER_THEMES[themeId];
                const selected = settings.theme === themeId;
                return (
                  <button
                    key={themeId}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected}
                    className="flex min-w-0 flex-col gap-2 rounded-2xl border border-white/10 bg-transparent p-2 text-left text-white contrast-more:border-2 contrast-more:border-current data-[selected=true]:border-[#ff3dae] data-[selected=true]:shadow-[0_0_0_1px_#ff3dae]"
                    onClick={() => onThemeChange(themeId)}
                  >
                    <span
                      className="relative flex h-[74px] flex-col justify-center gap-2 overflow-hidden rounded-xl border border-[rgba(17,24,39,0.08)] px-3.5 py-3"
                      style={{ background: theme.background, color: theme.text }}
                    >
                      <span className="text-lg font-semibold leading-none">Aa</span>
                      <span
                        className="block h-[3px] w-16 rounded-full"
                        style={{ background: theme.text, opacity: 0.85 }}
                      />
                      <span
                        className="block h-[3px] w-10 rounded-full"
                        style={{ background: theme.text, opacity: 0.45 }}
                      />
                      {selected && (
                        <span
                          className="absolute bottom-2 right-2 flex size-[22px] items-center justify-center rounded-full bg-[#ff3dae] text-white"
                          aria-hidden="true"
                        >
                          <Check className="size-3.5" />
                        </span>
                      )}
                    </span>
                    <span className="px-1 text-sm text-white">{theme.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-white">Размер текста</h3>
              <p className="mt-1 text-sm leading-5 text-[#9CA3AF]">Меняется только текст главы.</p>
            </div>
            <div
              className="inline-flex items-center gap-1 rounded-[14px] bg-[#11141c] p-1"
              role="group"
              aria-label="Размер текста"
            >
              <button
                type="button"
                className={settingsIconButtonClassName}
                aria-label="Уменьшить размер текста"
                disabled={settings.fontSize <= READER_FONT_SIZE_MIN}
                onClick={() => changeFontSize(settings.fontSize - READER_FONT_SIZE_STEP)}
              >
                <Minus className="size-4" />
              </button>
              <span className="w-14 text-center text-sm font-medium text-white">
                {settings.fontSize} px
              </span>
              <button
                type="button"
                className={settingsIconButtonClassName}
                aria-label="Увеличить размер текста"
                disabled={settings.fontSize >= READER_FONT_SIZE_MAX}
                onClick={() => changeFontSize(settings.fontSize + READER_FONT_SIZE_STEP)}
              >
                <Plus className="size-4" />
              </button>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white">Межстрочный интервал</h3>
                <p className="mt-1 text-sm leading-5 text-[#9CA3AF]">
                  Настройте плотность строк в тексте главы.
                </p>
              </div>
              <span className="rounded-full bg-[#11141C] px-3 py-1 text-sm font-medium text-white">
                {formatReaderLineHeight(settings.lineHeight)}
              </span>
            </div>
            <label className="mt-4 block">
              <span className="sr-only">Межстрочный интервал</span>
              <input
                className="h-7 w-full cursor-pointer appearance-none bg-transparent accent-[#ff3dae] [&::-moz-range-progress]:h-1 [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-[#ff3dae] [&::-moz-range-thumb]:h-[18px] [&::-moz-range-thumb]:w-[18px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#ff3dae] [&::-moz-range-track]:h-1 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-[#3a3f4b] [&::-webkit-slider-runnable-track]:h-1 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-[linear-gradient(90deg,#ff3dae_var(--reader-line-progress,0%),#3a3f4b_var(--reader-line-progress,0%))] [&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:h-[18px] [&::-webkit-slider-thumb]:w-[18px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-[#ff3dae] [&::-webkit-slider-thumb]:[box-shadow:0_0_0_4px_rgba(255,61,174,0.18)]"
                type="range"
                min={READER_LINE_HEIGHT_MIN}
                max={READER_LINE_HEIGHT_MAX}
                step={READER_LINE_HEIGHT_STEP}
                value={settings.lineHeight}
                aria-valuemin={READER_LINE_HEIGHT_MIN}
                aria-valuemax={READER_LINE_HEIGHT_MAX}
                aria-valuenow={settings.lineHeight}
                aria-valuetext={formatReaderLineHeight(settings.lineHeight)}
                style={{
                  ['--reader-line-progress' as string]: `${((settings.lineHeight - READER_LINE_HEIGHT_MIN) / (READER_LINE_HEIGHT_MAX - READER_LINE_HEIGHT_MIN)) * 100}%`,
                }}
                onChange={(event) => {
                  const snapped = snapReaderLineHeight(Number(event.target.value));
                  if (snapped === null) return;
                  onLineHeightChange(snapped);
                }}
              />
            </label>
            <div className="mt-1 flex justify-between text-xs text-[#9CA3AF]">
              <span>Плотнее</span>
              <span>Свободнее</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <button
            type="button"
            className="h-11 rounded-xl bg-[#2A3142] px-5 text-sm font-medium text-white"
            onClick={onReset}
          >
            Сбросить
          </button>
          <button
            type="button"
            className="h-11 rounded-xl bg-white px-6 text-sm font-semibold text-[#111827]"
            onClick={onClose}
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
};
